import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { User } from "react-feather";
import { FORMS, MODULES } from "../../const";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import { getOtherRefSearchDropdown } from "../../api/keyvalue";
import SearchDropdown from "../input/SearchDropdown";
import InfoList from "../InfoList";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";
import DateTimePickerInput from "../input/DateTimePickerInput";
import { getTags } from "../../api/tagApi";
import { issueValidation } from "../validations/IssueCreation";
import dayjs from "dayjs";
const FORM = FORMS.FLEET_ISSUE.ISSUE_MANAGEMENT.form;
const IssueForm = forwardRef(
({ errors, dropdown, isLoading, currentValues, editMode }, ref) => {

const userCompany = useUserCompany();
const [company, setCompany] = useState(
    userCompany ? userCompany.comId : null
);
const [issueData, setIssueData] = useState({
    topic: "",
    vehicle: "",
    vehicleId: null,
    description: "",
    label: [],
    primaryMeter: "",
    priority: "",
    reportDateTime: dayjs(),
    dueDate: "",
    primaryMeterDue: "",
    status: 1,
    fliActive: 1,
    company,
});
const [searchFieldReset, setSearchFieldReset] = useState(false);
const [currentErrors, setCurrentErrors] = useState({});
const [tagOptions, setTagOptions] = useState([]);
const [statusError, setStatusError] = useState("");
const [originalStatus, setOriginalStatus] = useState(null);

    const isOriginallyOpen = Number(originalStatus) === 1;
    const isOriginallyOnHold = Number(originalStatus) === 5;
    const disableAll = editMode && !isOriginallyOpen;
    const disableStatus = !editMode || (!isOriginallyOpen && !isOriginallyOnHold);


useEffect(() => {
    setCompany(userCompany ? userCompany.comId : null);
}, [userCompany]);

useEffect(() => {
    (async () => {
        const tags = await getTags();
        const formatted = tags.map(tag => ({
            key: tag.ftaId,
            value: tag.ftaId,
            text: tag.ftaLabel
        }));
        setTagOptions(formatted);
    })();
}, []);

useEffect(() => {
    if (editMode && currentValues) {
        setIssueData({
            topic: currentValues.fliTopic || "",
            vehicle: currentValues.vehicleNumber || "",
            vehicleId: currentValues.fliVehicle || null,
            description: currentValues.fliDescription || "",
            label: currentValues.fliLabel || [],
            primaryMeter: currentValues.fliPrimaryMeter != null
                ? String(currentValues.fliPrimaryMeter)
                : "",
            priority: currentValues.fliPriority || "",
            reportDateTime: currentValues.fliReportedTime
                ? dayjs(currentValues.fliReportedTime)
                : null,
            dueDate: currentValues.fliDueDate || "",
            primaryMeterDue: currentValues.fliPrimaryMeterDue != null
                ? String(currentValues.fliPrimaryMeterDue)
                : "",
            status: currentValues.fliStatus || "",
            id: currentValues.fliId,
            fliActive: currentValues.fliActive ?? 1,
            createdBy: currentValues.createdBy || "N/A",
            createdDate: currentValues.createdDate || "N/A",
            lastModifiedBy: currentValues.lastModifiedBy || "N/A",
            lastModifiedDate: currentValues.lastModifiedDate || "N/A"

        });
        setOriginalStatus(currentValues.fliStatus);
    }
}, [editMode, currentValues]);

useEffect(() => {
    setCurrentErrors(errors || {});
}, [errors]);

useImperativeHandle(ref, () => ({
    getData: () => issueData,

    validateForm: () => {
        const dataToValidate = {
            ...issueData,
            reportDateTime: issueData.reportDateTime
                ? dayjs(issueData.reportDateTime).toISOString()
                : "",
        };

        const errors = issueValidation(dataToValidate);

        if (issueData.reportDateTime && issueData.dueDate) {
            const report = dayjs(issueData.reportDateTime);
            const due = dayjs(issueData.dueDate);

            if (due.isBefore(report, "day")) {
                errors.dueDate = "Due Date must be same or after Report Date";
            }
        }

        if (
            issueData.primaryMeter !== "" &&
            issueData.primaryMeterDue !== ""
        ) {
            const meter = parseFloat(issueData.primaryMeter);
            const meterDue = parseFloat(issueData.primaryMeterDue);

            if (!isNaN(meter) && !isNaN(meterDue) && meterDue < meter) {
                errors.primaryMeterDue = "Primary Meter Due cannot be less than Primary Meter";
            }
        }

        // Character limit validations
        if (issueData.topic && issueData.topic.length > 100) {
            errors.topic = "Topic cannot exceed 100 characters";
        }

        if (issueData.description && issueData.description.length > 1000) {
            errors.description = "Description cannot exceed 1000 characters";
        }

        if (editMode) {
            const isCurrentlyOpen = Number(originalStatus) === 1;
            const isCurrentlyOnHold = Number(originalStatus) === 5;

            const selectedOnHold = Number(issueData.status) === 5;
            const selectedOpen = Number(issueData.status) === 1;
            const statusUnchanged = Number(issueData.status) === Number(originalStatus);

            if (!statusUnchanged) {
                if (isCurrentlyOpen && !selectedOnHold) {
                    errors.status = "From Open status, you can only change to 'On Hold'";
                }

                if (isCurrentlyOnHold && !selectedOpen) {
                    errors.status = "From On Hold status, you can only change to 'Open'";
                }
            }
        }

        return errors;
    },

    setErrors: (errs) => {
        setCurrentErrors(errs || {});
    },

    resetForm: () => {
        setIssueData({
            topic: "",
            vehicle: "",
            vehicleId: null,
            description: "",
            label: [],
            primaryMeter: "",
            priority: "",
            reportDateTime: dayjs(),
            dueDate: "",
            primaryMeterDue: "",
            status: "",
            fliActive: 1,
        });
    },
}));

return (
<>
{isLoading ? (
    <LoadingSpinner />
) : (

<form className="ui form" style={{ overflowY: "auto", height: "75vh" }}>
<div className="ui segment">
<h4 className="ui dividing header">Issue Management</h4>

{/* TOP */}
<div className="two fields">
<Input
    label="Topic"
    disabled={disableAll}
    required
    error={currentErrors?.topic}
    resetError={() => {
        const { topic, ...rest } = currentErrors;
        setCurrentErrors(rest);
    }}
    module={MODULES.Fleet}
    form={FORM}
    field="topic"
    editMode={editMode}
    value={issueData.topic}
    onChange={(e) =>
        setIssueData(prev => ({
            ...prev,
            topic: e.target.value
        }))
    }
/>

<div className={`field ${currentErrors?.vehicleId ? "error" : ""}`}>
<SearchDropdown
    key={issueData.vehicle}
    module={MODULES.Fleet}
    disabled={disableAll}
    form={FORM}
    required
    field="vehicle"
    editMode={editMode}
    reset={searchFieldReset}
    setReset={setSearchFieldReset}
    api={getOtherRefSearchDropdown}
    apiDependency={{ type: "vehicle", value: "" }}
    defaultValue={issueData.vehicle || ""}
    onResultSelect={(data) => {
        setIssueData(prev => ({
            ...prev,
            vehicleId: data,
            vehicle: prev.vehicle || ""
        }));

        const { vehicleId, ...rest } = currentErrors;
        setCurrentErrors(rest);
    }}
    onClear={() => {
        setIssueData(prev => ({
            ...prev,
            vehicleId: null,
            vehicle: ""
        }));
    }}
    label="Vehicle"
/>

{currentErrors?.vehicleId && (
    <div className="ui pointing red basic label">
        {currentErrors.vehicleId}
    </div>
)}
</div>
</div>

<div className="two fields">
<Input
    required
    label="Description"
    disabled={disableAll}
    error={currentErrors?.description}
    resetError={() => {
        const { description, ...rest } = currentErrors;
        setCurrentErrors(rest);
    }}
    value={issueData.description}
    onChange={(e) =>
        setIssueData(prev => ({
            ...prev,
            description: e.target.value
        }))
    }
/>

<Dropdown
    label="Labels"
    placeholder="Select Tags"
    fluid
    disabled={disableAll}
    multiple
    search
    selection
    options={tagOptions}
    value={issueData.label || []}
    onChange={(e, data) => {
        setIssueData(prev => ({
            ...prev,
            label: data.value
        }));
    }}
    style={{
        minHeight: "38px",
        height: "auto",
        display: "flex",
        alignItems: "flex-start",
        flexWrap: "wrap"
    }}
/>
</div>

{/* Primary Meter + Primary Meter Due */}
<div className="two fields">
<Input
    label="Primary Meter"
    disabled={disableAll}
    error={currentErrors?.primaryMeter}
    resetError={() => {
        const { primaryMeter, ...rest } = currentErrors;
        setCurrentErrors(rest);
    }}
    value={issueData.primaryMeter}
    onChange={(e) =>
        setIssueData(prev => ({
            ...prev,
            primaryMeter: e.target.value
        }))
    }
/>

<Input
    label="Primary Meter Due"
    disabled={disableAll}
    error={currentErrors?.primaryMeterDue}
    resetError={() => {
        const { primaryMeterDue, ...rest } = currentErrors;
        setCurrentErrors(rest);
    }}
    value={issueData.primaryMeterDue}
    onChange={(e) =>
        setIssueData(prev => ({
            ...prev,
            primaryMeterDue: e.target.value
        }))
    }
/>
</div>

{/* Report Date + Due Date */}
<div className="two fields">
    <DateTimePickerInput
        required
        label="Report Date & Time"
        disabled={disableAll}
        error={currentErrors?.reportDateTime}
        value={issueData.reportDateTime}
        validators={[
            {
                func: (date) => !dayjs(date).isAfter(dayjs()),
                message: "Date & Time cannot be selected in the future"
            }
        ]}
        onAccept={(value) => {
            setIssueData(prev => ({
                ...prev,
                reportDateTime: value
            }));

            setCurrentErrors(prev => {
                const { reportDateTime, ...rest } = prev;
                return rest;
            });
        }}
    />

<Input
    required
    type="date"
    label="Due Date"
    disabled={disableAll}
    min={dayjs().format("YYYY-MM-DD")}
    error={currentErrors?.dueDate}
    resetError={() => {
        const { dueDate, ...rest } = currentErrors;
        setCurrentErrors(rest);
    }}
    value={issueData.dueDate}
    onChange={(e) =>
        setIssueData(prev => ({
            ...prev,
            dueDate: e.target.value
        }))
    }
/>
</div>

{/* Priority + Status */}
<div className="two fields">
<Dropdown
    required
    label="Priority"
    disabled={disableAll}
    error={currentErrors?.priority}
    resetError={() => {
        const { priority, ...rest } = currentErrors;
        setCurrentErrors(rest);
    }}
    options={dropdown?.priorityOptions || []}
    value={issueData.priority}
    onChange={(e, d) =>
        setIssueData(prev => ({
            ...prev,
            priority: d.value
        }))
    }
/>

<Dropdown
    label="Status"
    disabled={disableStatus}
    error={currentErrors?.status}
    resetError={() => {
        const { status, ...rest } = currentErrors;
        setCurrentErrors(rest);
    }}
    options={dropdown?.statusOptions || []}
    value={issueData.status}
    onChange={(e, d) =>
        setIssueData(prev => ({
            ...prev,
            status: d.value
        }))
    }
/>
</div>
    <div className="two fields">
        <Dropdown
            required
            label="Active"
            error={currentErrors?.fliActive}
            resetError={() => {
                const { fliActive, ...rest } = currentErrors;
                setCurrentErrors(rest);
            }}
            options={dropdown?.activeOptions || []}
            value={issueData.fliActive}
            disabled={disableAll}
            field="fliActive"
            needDeletePermission={true}
            editMode={editMode}
            module={MODULES.Fleet}
            form={FORM}
            onChange={(e, d) =>
                setIssueData(prev => ({
                    ...prev,
                    fliActive: d.value
                }))
            }
        />
    </div>


</div>

{editMode && (
<InfoList
    title="User Information"
    content={[
        { title: "Created By", value: issueData.createdBy || "N/A" },
        { title: "Created Date", value: issueData.createdDate ? formatFromJsonDate(issueData.createdDate) : "N/A" },
        { title: "Last Modified By", value: issueData.lastModifiedBy || "N/A" },
        {
            title: "Last Modified Date",
            value:
                issueData.lastModifiedDate &&
                !isNaN(new Date(issueData.lastModifiedDate).getTime())
                    ? formatFromJsonDate(issueData.lastModifiedDate)
                    : "N/A"
        },
    ]}
    Icon={User}
/>
)}

</form>

)}
</>
);
});

export default IssueForm;
